/**
 * Support yearly digest — at the annual fiscal close, every hired support
 * role gets a chance to send the patron a personalised end-of-year message
 * grounded in actual KPIs from the closing year.
 *
 * Design principles:
 *   • Each role's message reflects their domain. Midal talks IS,
 *     Nadia Cheikh talks turnover, Marc Dupuis talks contracts won.
 *   • Tone is calibrated by outcome: warm/celebratory when KPIs are good,
 *     pince-sans-rire or supportive when they're not.
 *   • Nathalie Robert (assistante administrative) ALWAYS sends a message
 *     — her role is to give the patron a synthesis. The other roles only
 *     send when they have something concrete to say.
 *
 * Returns an array of Messagerie payloads ready for `pushCustomMessage`.
 * Empty array when no hired support has anything to say.
 *
 * 31/05/2026 — narrative bonus pattern extended to all support roles.
 */

const TTL_DAYS = 30;

const fmt = (n) => Math.round(n || 0).toLocaleString('fr-FR');
const fmtK = (n) => {
  const v = Math.round((n || 0) / 1000);
  return `${v.toLocaleString('fr-FR')} k€`;
};
const isHired = (state, roleId) => !!(state.hiredSupport || {})[roleId];

/* ─────────────────────────────────────────────────────────────────────
 * Per-role builders. Each returns a payload OR null.
 * `kpis` is the canonical year-end snapshot derived from the annual
 * report + live state.
 * ───────────────────────────────────────────────────────────────────── */

function buildNathalieRobert(state, kpis) {
  // Always speaks at year-end — admin synthesis is literally her job.
  const {
    year, totalRevenue, contractsCompleted, vehiclesTotal, driversTotal, treasuryDelta,
    transfersInitiatedThisYear, holidaysGrantedThisYear, fuelTheftsResolvedThisYear, vehiclesScrappedThisYear,
  } = kpis;
  const trendIcon = treasuryDelta >= 0 ? '↗' : '↘';
  const trendWord = treasuryDelta >= 0 ? 'positive' : 'en repli';
  // 17/06/2026 (Lot 4) — bloc événementiel (Nathalie agrège tout pour la
  // direction). Seules les lignes non-nulles sont incluses pour rester
  // concis si l'année a été calme.
  const events = [];
  if (transfersInitiatedThisYear > 0) {
    events.push(`• **Transferts inter-dépôts** : ${transfersInitiatedThisYear} opération${transfersInitiatedThisYear > 1 ? 's' : ''} (Rémi Forni)`);
  }
  if (holidaysGrantedThisYear > 0) {
    events.push(`• **Congés payés** : ${holidaysGrantedThisYear} jour${holidaysGrantedThisYear > 1 ? 's' : ''}-congé octroyé${holidaysGrantedThisYear > 1 ? 's' : ''}`);
  }
  if (vehiclesScrappedThisYear > 0) {
    events.push(`• **Véhicules réformés** : ${vehiclesScrappedThisYear} (sortie du parc)`);
  }
  if (fuelTheftsResolvedThisYear > 0) {
    events.push(`• **Dossiers disciplinaires** : ${fuelTheftsResolvedThisYear} (détournement carburant)`);
  }
  const eventsBlock = events.length > 0
    ? `\n\n**Faits marquants ${year}** :\n${events.join('\n')}`
    : '';

  const body =
    `Bonjour Patron,\n\n` +
    `Pour la synthèse annuelle de l'exercice **${year}**, voici les chiffres que je classe ce matin :\n\n` +
    `• **CA total** : ${fmt(totalRevenue)} €\n` +
    `• **Contrats livrés** : ${contractsCompleted}\n` +
    `• **Flotte au 31/12** : ${vehiclesTotal} véhicule${vehiclesTotal > 1 ? 's' : ''}\n` +
    `• **Effectif chauffeurs** : ${driversTotal}\n` +
    `• **Trésorerie ${trendIcon}** : variation ${trendWord} de **${fmt(Math.abs(treasuryDelta))} €**${eventsBlock}\n\n` +
    `J'ai classé les factures, archivé les justificatifs et préparé les dossiers pour le comptable. ` +
    `Tout est rangé, vous pouvez attaquer la nouvelle année sereinement.\n\n` +
    `Bonne année !\n\n` +
    `— Nathalie Robert, votre assistante administrative`;
  return {
    senderType: 'support',
    senderName: 'Nathalie Robert (Assistante)',
    senderRole: 'role-nathalie-robert',
    tone: 'friendly',
    kind: 'info',
    subject: `Synthèse annuelle ${year} — tout est classé`,
    body,
    ttlDays: TTL_DAYS,
  };
}

function buildChristopheFaure(state, kpis) {
  const { year, contractsCompleted, reputation, vehiclesTotal, transfersInitiatedThisYear } = kpis;
  // Threshold: a serious exploitation manager expects > 8 contracts/vehicle/year.
  const perVehicle = vehiclesTotal > 0 ? contractsCompleted / vehiclesTotal : 0;
  const strong = perVehicle >= 8 && reputation >= 60;
  const weak = perVehicle < 4 || reputation < 40;
  // 17/06/2026 (Lot 4) — Rémi évoque ses transferts inter-dépôts automatisés.
  // Ligne dédiée si > 0 transferts, sinon il glisse une mention discrète.
  const transfersLine = transfersInitiatedThisYear > 0
    ? `\n\nCôté logistique inter-dépôts : j'ai automatiquement orchestré **${transfersInitiatedThisYear} transfert${transfersInitiatedThisYear > 1 ? 's' : ''}** de camions cette année pour servir les contrats hors zone. Sans ça, on aurait laissé filer ces opportunités à la concurrence.`
    : (vehiclesTotal > 0 && (state.ownedDepots || []).length > 1
        ? `\n\nNote : aucun transfert inter-dépôts déclenché cette année — soit la couverture géographique tenait, soit on a laissé passer des contrats hors zone faute d'arbitrage.`
        : '');
  let subject, opener, advice;
  if (strong) {
    subject = `Exploitation ${year} — du bon boulot`;
    opener = `Patron, ${contractsCompleted} contrats sur ${vehiclesTotal} véhicule${vehiclesTotal > 1 ? 's' : ''} (${perVehicle.toFixed(1)}/véh), réputation ${reputation}/100 : **on a tenu la cadence**.`;
    advice = `Pour l'année prochaine je propose un audit tournée par tournée — il y a probablement 5-8 % de gains kilométriques à aller chercher.`;
  } else if (weak) {
    subject = `Exploitation ${year} — on peut mieux faire`;
    opener = `Patron, l'exercice ${year} se boucle sur **${perVehicle.toFixed(1)} contrats/véhicule** (objectif ≥ 8) et une réputation à **${reputation}/100**.`;
    advice = `Trois leviers à étudier : taux de chargement retour (camions vides), planification chauffeur (temps morts), et la spécialisation flotte vs panier de contrats.`;
  } else {
    subject = `Exploitation ${year} — bilan honnête`;
    opener = `Patron, ${contractsCompleted} contrats livrés sur ${vehiclesTotal} véhicule${vehiclesTotal > 1 ? 's' : ''}. Pas de quoi pavoiser, pas de quoi rougir.`;
    advice = `Pour l'année qui vient, le combat sera sur l'occupation flotte — viser 85 % plutôt que les ~75 % actuels.`;
  }
  return {
    senderType: 'support',
    senderName: 'Rémi Forni (Resp. exploitation)',
    senderRole: 'role-christophe-faure',
    tone: strong ? 'celebratory' : (weak ? 'warning' : 'advice'),
    kind: 'info',
    subject,
    body: `Patron,\n\n${opener}\n\n${advice}${transfersLine}\n\nÀ vos ordres,\n— Rémi Forni`,
    ttlDays: TTL_DAYS,
  };
}

function buildNadiaCheikh(state, kpis) {
  const {
    year, firedDriversCount, attritionThisYear, driversTotal,
    holidaysGrantedThisYear, holidayDriversWentOnLeaveThisYear, fuelTheftsResolvedThisYear,
  } = kpis;
  // No-op if no hires at all on the year (nothing to comment).
  if (driversTotal === 0 && firedDriversCount === 0) return null;
  const churn = firedDriversCount + attritionThisYear;
  const ratio = driversTotal > 0 ? churn / (driversTotal + churn) : 0;
  let subject, opener, advice, tone;
  if (ratio >= 0.30 || churn >= 3) {
    subject = `Climat social ${year} — il faut qu'on parle`;
    opener = `Patron, ${churn} chauffeur${churn > 1 ? 's' : ''} perdu${churn > 1 ? 's' : ''} cette année (démissions + licenciements). C'est ${Math.round(ratio * 100)} % de turnover.`;
    advice = `On aurait pu en garder au moins la moitié avec deux cafés et un peu d'écoute. La prochaine fois qu'un chauffeur passe sous **30 de satisfaction**, je vous remonte l'info — n'attendez pas qu'il claque la porte pour réagir.`;
    tone = 'warning';
  } else if (churn === 0 && driversTotal >= 3) {
    subject = `Climat social ${year} — zéro départ, bravo`;
    opener = `Patron, **aucun départ** cette année sur ${driversTotal} chauffeurs. C'est rare dans le métier.`;
    advice = `On garde le cap : entretiens annuels en janvier, prime de fidélité au passage des 2 ans, et on surveille les niveaux de satisfaction au-dessus de 60.`;
    tone = 'celebratory';
  } else {
    subject = `Climat social ${year} — tient la route`;
    opener = `Patron, ${churn} départ${churn > 1 ? 's' : ''} sur l'année, ${driversTotal} chauffeur${driversTotal > 1 ? 's' : ''} en poste au 31/12. Turnover gérable.`;
    advice = `Pour la nouvelle année, je propose un entretien individuel systématique — ça désamorce 80 % des départs avant qu'ils ne s'enclenchent.`;
    tone = 'advice';
  }

  // 17/06/2026 (Lot 4) — bloc Congés gérés (KPI dédié) :
  let leaveBlock = '';
  if (holidaysGrantedThisYear > 0) {
    const leaveAvg = holidayDriversWentOnLeaveThisYear > 0
      ? Math.round(holidaysGrantedThisYear / holidayDriversWentOnLeaveThisYear)
      : 0;
    leaveBlock =
      `\n\n**Gestion des congés (${year})** : ` +
      `${holidaysGrantedThisYear} jour${holidaysGrantedThisYear > 1 ? 's' : ''}-congé octroyé${holidaysGrantedThisYear > 1 ? 's' : ''} ` +
      `sur ${holidayDriversWentOnLeaveThisYear} chauffeur${holidayDriversWentOnLeaveThisYear > 1 ? 's' : ''} (~${leaveAvg} j/chauffeur). ` +
      `Tout s'est déroulé sans interruption d'activité majeure — j'ai séquencé les départs pour préserver les rotations.`;
  } else if (driversTotal > 0) {
    leaveBlock =
      `\n\n**Gestion des congés (${year})** : aucun départ déclenché — attention, ` +
      `les soldes s'accumulent et la pression sociale va monter au prochain exercice.`;
  }

  // 17/06/2026 (Lot 4) — bloc Arc vol-de-carburant si géré cette année :
  let fraudBlock = '';
  if (fuelTheftsResolvedThisYear > 0) {
    fraudBlock =
      `\n\n**Volets disciplinaires** : ${fuelTheftsResolvedThisYear} dossier${fuelTheftsResolvedThisYear > 1 ? 's' : ''} de détournement de carburant traité${fuelTheftsResolvedThisYear > 1 ? 's' : ''} ` +
      `cette année (licenciement faute grave ou rupture conventionnelle). Le climat de confiance reste à reconstruire — ` +
      `je propose un audit mensuel des consommations carburant sur les 12 prochains mois.`;
  }

  return {
    senderType: 'support',
    senderName: 'Nadia Cheikh (Resp. RH)',
    senderRole: 'role-nadia-cheikh',
    tone,
    kind: 'info',
    subject,
    body: `Patron,\n\n${opener}\n\n${advice}${leaveBlock}${fraudBlock}\n\n— Nadia Cheikh`,
    ttlDays: TTL_DAYS,
  };
}

function buildBrunoLavergne(state, kpis) {
  const { year, breakdownsThisYear, vehiclesTotal, vehiclesScrappedThisYear } = kpis;
  if (vehiclesTotal === 0) return null;
  const perVehicle = breakdownsThisYear / vehiclesTotal;
  let subject, opener, advice, tone;
  if (perVehicle <= 0.5) {
    subject = `Atelier ${year} — flotte saine`;
    opener = `Patron, **${breakdownsThisYear}** panne${breakdownsThisYear > 1 ? 's' : ''} sur ${vehiclesTotal} véhicule${vehiclesTotal > 1 ? 's' : ''} cette année. Le diag préventif fait son job.`;
    advice = `On continue : huile tous les 30 000 km, contrôle freins tous les 6 mois, et on remplace les pièces fatiguées AVANT qu'elles ne lâchent.`;
    tone = 'celebratory';
  } else if (perVehicle >= 1.5) {
    subject = `Atelier ${year} — flotte fatiguée`;
    opener = `Patron, **${breakdownsThisYear} pannes** sur ${vehiclesTotal} véhicule${vehiclesTotal > 1 ? 's' : ''}, soit ${perVehicle.toFixed(1)}/véhicule. C'est trop.`;
    advice = `Deux véhicules au-dessus de 80 % d'usure réelle sortent du parc en priorité. Je vous fais une liste, vous décidez : revente ou hypothèque + remplacement.`;
    tone = 'warning';
  } else {
    subject = `Atelier ${year} — bilan correct`;
    opener = `Patron, ${breakdownsThisYear} panne${breakdownsThisYear > 1 ? 's' : ''} sur l'année. Rien d'alarmant.`;
    advice = `Surveillez l'usure des 2-3 camions les plus anciens. Au-delà de 85 %, le risque mécanique grimpe en flèche.`;
    tone = 'advice';
  }
  // 17/06/2026 (Lot 4) — bloc véhicules réformés (Bruno Lavergne KPI).
  const scrapBlock = vehiclesScrappedThisYear > 0
    ? `\n\n**Véhicules réformés** : ${vehiclesScrappedThisYear} camion${vehiclesScrappedThisYear > 1 ? 's' : ''} sorti${vehiclesScrappedThisYear > 1 ? 's' : ''} du parc cette année (casse / réforme moteur). Récupération à 10 % du prix neuf — c'est la limite du modèle quand on attend trop longtemps.`
    : '';
  return {
    senderType: 'support',
    senderName: 'Bruno Lavergne (Chef d\'atelier)',
    senderRole: 'role-bruno-lavergne',
    tone,
    kind: 'info',
    subject,
    body: `Patron,\n\n${opener}\n\n${advice}${scrapBlock}\n\n— Bruno Lavergne`,
    ttlDays: TTL_DAYS,
  };
}

function buildSylvieMarchand(state, kpis) {
  const { year, totalRevenue, contractsCompleted, reputationDelta, avgContractValue } = kpis;
  // 22/07/2026 — panier moyen RÉEL des contrats livrés (ledger) quand
  // disponible : l'ancien totalRevenue/contractsCompleted donnait des
  // paniers aberrants (453 268 €) car le compteur était sous-évalué.
  const avgContract = avgContractValue ?? (contractsCompleted > 0 ? totalRevenue / contractsCompleted : 0);
  let subject, opener, advice, tone;
  if (avgContract >= 5000 && reputationDelta >= 0) {
    subject = `Commerce ${year} — montée en gamme réussie`;
    opener = `Patron, panier moyen contrat à **${fmt(avgContract)} €** et réputation en hausse de **${reputationDelta}** points. Notre positionnement premium se confirme.`;
    advice = `Pour la nouvelle année, j'ouvre la chasse aux grands comptes que vous savez — celui du Beaujolais qui veut un transporteur dédié, et les Vendéens des produits frais.`;
    tone = 'celebratory';
  } else if (avgContract < 2500) {
    subject = `Commerce ${year} — il faut remonter en gamme`;
    opener = `Patron, panier moyen à **${fmt(avgContract)} €** sur ${contractsCompleted} contrats. On est dans le commodity, on travaille beaucoup pour pas grand-chose.`;
    advice = `Filtrez les contrats classiques < 1 500 €/contrat. Je vous propose un focus grands comptes au T1 — 2-3 prospects bien préparés, et on change de catégorie.`;
    tone = 'warning';
  } else {
    subject = `Commerce ${year} — solide, on consolide`;
    opener = `Patron, ${contractsCompleted} contrats, panier moyen ${fmt(avgContract)} €. C'est tenu.`;
    advice = `Année qui vient : objectif +20 % de panier moyen via la spécialisation export EU et frigorifique.`;
    tone = 'advice';
  }
  return {
    senderType: 'support',
    senderName: 'Sylvie Marchand (Dir. commerciale)',
    senderRole: 'role-sylvie-marchand',
    tone,
    kind: 'info',
    subject,
    body: `Patron,\n\n${opener}\n\n${advice}\n\n— Sylvie Marchand`,
    ttlDays: TTL_DAYS,
  };
}

function buildAichaDiallo(state, kpis) {
  const { year, reputation, reputationDelta, notorietyDelta } = kpis;
  let subject, opener, advice, tone;
  if (reputationDelta >= 5 || notorietyDelta >= 8) {
    subject = `Communication ${year} — la marque monte`;
    opener = `Patron, on termine l'année à **${reputation}/100** de réputation (${reputationDelta >= 0 ? '+' : ''}${reputationDelta} pts) et notoriété en hausse de **${notorietyDelta}**.`;
    advice = `Idée pour ${year + 1} : un dossier presse régionale sur notre engagement RSE (flotte électrique partielle, formation chauffeurs). 3 articles bien placés et on consolide.`;
    tone = 'celebratory';
  } else if (reputationDelta <= -8) {
    subject = `Communication ${year} — chute de réputation à expliquer`;
    opener = `Patron, on perd **${Math.abs(reputationDelta)}** points de réputation cette année. Aujourd'hui : ${reputation}/100.`;
    advice = `Trois pistes : une communication proactive sur la sécurité (réponse aux pannes / incidents), un sondage clients pour identifier les douleurs, et — si vraiment grave — un communiqué officiel.`;
    tone = 'warning';
  } else {
    return null; // Nothing dramatic to comment on.
  }
  return {
    senderType: 'support',
    senderName: 'Aïcha Diallo (Attachée de presse)',
    senderRole: 'role-aicha-diallo',
    tone,
    kind: 'info',
    subject,
    body: `Patron,\n\n${opener}\n\n${advice}\n\n— Aïcha Diallo`,
    ttlDays: TTL_DAYS,
  };
}

function buildSophieLambert(state, kpis) {
  const { year, totalIsPaid, isOptimizationsActive } = kpis;
  if (totalIsPaid <= 0) return null;
  let subject, opener, advice, tone;
  if (isOptimizationsActive >= 2) {
    subject = `Comptabilité ${year} — IS bien optimisé`;
    opener = `Patron, **${fmt(totalIsPaid)} €** d'IS payés cette année avec **${isOptimizationsActive}** optimisations actives. On a fait le travail.`;
    advice = `Pour ${year + 1}, jetez un œil au crédit d'impôt formation (si on intensifie la montée en compétence des chauffeurs) et à l'amortissement accéléré sur la prochaine acquisition véhicule.`;
    tone = 'celebratory';
  } else {
    subject = `Comptabilité ${year} — optimisations à creuser`;
    opener = `Patron, **${fmt(totalIsPaid)} €** d'IS payés cette année. ${isOptimizationsActive === 0 ? 'Aucune' : 'Une seule'} optimisation active — il y a de la marge.`;
    advice = `Trois pistes pour ${year + 1} : (1) amortissement accéléré sur les véhicules neufs (économie immédiate), (2) crédit impôt formation (si on a un plan formation chauffeurs), (3) si Midal est en poste, voir avec lui le mécanisme de Réserve fiscale 25 %.`;
    tone = 'advice';
  }
  return {
    senderType: 'support',
    senderName: 'Sophie Lambert (Comptable)',
    senderRole: 'role-sophie-lambert',
    tone,
    kind: 'info',
    subject,
    body: `Patron,\n\n${opener}\n\n${advice}\n\n— Sophie Lambert`,
    ttlDays: TTL_DAYS,
  };
}

/* ─────────────────────────────────────────────────────────────────────
 * 12 additional builders (commerce juniors, marketing, communication
 * juniors, achats, finance senior, RH chasseur, SAV, garage junior).
 * Each speaks only when their domain has something concrete to say.
 * ───────────────────────────────────────────────────────────────────── */

function buildLeaBertrand(state, kpis) {
  const { year, contractsCompleted } = kpis;
  if (contractsCompleted === 0) return null;
  const tone = contractsCompleted >= 15 ? 'celebratory' : 'advice';
  const subject = tone === 'celebratory'
    ? `Prospection ${year} — terrain bien labouré`
    : `Prospection ${year} — on peut accélérer`;
  const opener = tone === 'celebratory'
    ? `Patron, ${contractsCompleted} contrats clôturés cette année — j'ai aimé courir le terrain pour vous.`
    : `Patron, ${contractsCompleted} contrats sur l'année. Pas mal mais on peut doubler le rythme.`;
  const advice = tone === 'celebratory'
    ? `Pour ${year + 1}, je vise les zones péri-urbaines qu'on a sous-explorées (Z.A. autour des sorties d'autoroute) — beaucoup d'artisans qui externalisent leurs livraisons.`
    : `Mon plan pour ${year + 1} : phoning quotidien (10 appels/jour), 2 rendez-vous terrain/semaine, et qualification serrée des prospects avant rendez-vous. On peut doubler le pipe en 6 mois.`;
  return {
    senderType: 'support',
    senderName: 'Léa Bertrand (Commerciale)',
    senderRole: 'role-lea-bertrand',
    tone,
    kind: 'info',
    subject,
    body: `Patron,\n\n${opener}\n\n${advice}\n\n— Léa Bertrand`,
    ttlDays: TTL_DAYS,
  };
}

function buildMarcDupuis(state, kpis) {
  const { year, totalRevenue, contractsCompleted, avgContractValue } = kpis;
  // 22/07/2026 — même correction panier moyen que Sylvie (ledger réel).
  const avgContract = avgContractValue ?? (contractsCompleted > 0 ? totalRevenue / contractsCompleted : 0);
  const tone = avgContract >= 4000 ? 'celebratory' : 'advice';
  const subject = tone === 'celebratory'
    ? `Démarchage ${year} — paniers musclés`
    : `Démarchage ${year} — on monte le panier`;
  const body =
    `Patron,\n\n` +
    `Bilan terrain ${year} : ${contractsCompleted} contrats, panier moyen **${fmt(avgContract)} €**. ` +
    (tone === 'celebratory'
      ? `Les salons régionaux que nous avons faits ensemble ont payé — on a placé du contrat à valeur.`
      : `Beaucoup de petits contrats — l'année prochaine je propose qu'on cible 3-4 salons régionaux ciblés (TRM, Logistic Show régional) pour capter du dossier > 5 k€.`) +
    `\n\n` +
    `Côté pipe ${year + 1}, j'ai 8 prospects qualifiés à relancer en janvier. À votre dispo pour les RDV terrain.\n\n` +
    `— Marc Dupuis`;
  return {
    senderType: 'support',
    senderName: 'Marc Dupuis (Commercial terrain)',
    senderRole: 'role-marc-dupuis',
    tone,
    kind: 'info',
    subject,
    body,
    ttlDays: TTL_DAYS,
  };
}

function buildYanisElKaroui(state, kpis) {
  const { year, notoriety, notorietyDelta } = kpis;
  if (Math.abs(notorietyDelta) < 3) return null; // Nothing to brag/worry about.
  const tone = notorietyDelta > 0 ? 'celebratory' : 'advice';
  const subject = tone === 'celebratory'
    ? `Présence digitale ${year} — la marque infuse`
    : `Présence digitale ${year} — on relance le plan`;
  const opener = tone === 'celebratory'
    ? `Patron, notoriété en hausse de **${notorietyDelta}** points (aujourd'hui ${notoriety}/100). Les posts LinkedIn fonctionnent.`
    : `Patron, notoriété en baisse de **${Math.abs(notorietyDelta)}** points (aujourd'hui ${notoriety}/100). Faut qu'on remette du contenu en ligne.`;
  const advice = tone === 'celebratory'
    ? `Pour ${year + 1}, je propose 2 vidéos coulisses par trimestre (un chauffeur, un mécanicien, un planning…) — c'est ce qui marche le mieux.`
    : `Plan ${year + 1} : 1 post LinkedIn par semaine (humain, pas corpo), reprise du compte Instagram, et un mini-reportage vidéo pour reconnecter avec le marché.`;
  return {
    senderType: 'support',
    senderName: 'Yanis El Karoui (Marketing)',
    senderRole: 'role-yanis-elkaroui',
    tone,
    kind: 'info',
    subject,
    body: `Patron,\n\n${opener}\n\n${advice}\n\n— Yanis`,
    ttlDays: TTL_DAYS,
  };
}

function buildCamilleRoussel(state, kpis) {
  const { year, notoriety, notorietyDelta } = kpis;
  const tone = notorietyDelta >= 6 ? 'celebratory' : (notorietyDelta <= -5 ? 'warning' : 'advice');
  const subject = `Stratégie de marque ${year}`;
  const opener = tone === 'celebratory'
    ? `Patron, **${notorietyDelta} pts de notoriété** sur l'année. La promesse de marque commence à infuser.`
    : tone === 'warning'
      ? `Patron, **-${Math.abs(notorietyDelta)} pts de notoriété**. Il faut qu'on parle positionnement.`
      : `Patron, notoriété stable à **${notoriety}/100**. On consolide.`;
  const advice = tone === 'celebratory'
    ? `Objectif ${year + 1} : passer en mode "thought leadership" — un livre blanc transport-décarbonation, 3 conférences sectorielles, et un partenariat presse régionale.`
    : tone === 'warning'
      ? `Trois leviers urgents : (1) refonte du site (vitrine vieillissante), (2) ligne éditoriale resserrée (focus "fiabilité PME"), (3) audit perception client en T1.`
      : `Pour faire monter la marque d'un cran, je propose un rebranding léger (logo, couleurs, ton) couplé à une campagne LinkedIn ciblée. Budget 15-20 k€.`;
  return {
    senderType: 'support',
    senderName: 'Sherazade Fetitou (Resp. marketing)',
    senderRole: 'role-sherazade-fetitou',
    tone,
    kind: 'info',
    subject,
    body: `Patron,\n\n${opener}\n\n${advice}\n\n— Sherazade Fetitou`,
    ttlDays: TTL_DAYS,
  };
}

function buildThomasVidal(state, kpis) {
  const { year, reputationDelta } = kpis;
  if (Math.abs(reputationDelta) < 4) return null;
  const tone = reputationDelta > 0 ? 'celebratory' : 'warning';
  const subject = tone === 'celebratory'
    ? `Réseaux ${year} — communauté positive`
    : `Réseaux ${year} — on a essuyé des plâtres`;
  const opener = tone === 'celebratory'
    ? `Patron, on termine ${year} avec **+${reputationDelta} pts de réputation**. Les retours en ligne sont bons.`
    : `Patron, **-${Math.abs(reputationDelta)} pts de réputation**. Quelques avis négatifs ont pesé.`;
  const advice = tone === 'celebratory'
    ? `On continue : 1 post hebdo, réponse à 100 % des commentaires < 24 h, et on lance un programme "ambassadeurs chauffeurs" — 3 contenus témoignages par trimestre.`
    : `Plan crise ${year + 1} : (1) réponse personnalisée à TOUT avis < 4 étoiles, (2) refonte du process de gestion des réclamations en lien avec Inès, (3) campagne "transparence" — vlog mensuel sur les coulisses.`;
  return {
    senderType: 'support',
    senderName: 'Thomas Vidal (Community Manager)',
    senderRole: 'role-thomas-vidal',
    tone,
    kind: 'info',
    subject,
    body: `Patron,\n\n${opener}\n\n${advice}\n\n— Thomas Vidal`,
    ttlDays: TTL_DAYS,
  };
}

function buildKarimBenali(state, kpis) {
  const { year, vehiclesTotal } = kpis;
  if (vehiclesTotal === 0) return null;
  return {
    senderType: 'support',
    senderName: 'Karim Benali (Acheteur)',
    senderRole: 'role-karim-benali',
    tone: 'advice',
    kind: 'info',
    subject: `Achats ${year} — revue fournisseurs`,
    body:
      `Patron,\n\n` +
      `Bilan achats ${year} : ${vehiclesTotal} véhicule${vehiclesTotal > 1 ? 's' : ''} actif${vehiclesTotal > 1 ? 's' : ''}, ` +
      `négociations carburant en cours avec 2 stations partenaires régionales. ` +
      `Économie réalisée sur la pompe : ~5 % vs. tarif boursier.\n\n` +
      `Pour ${year + 1}, je vise un troisième fournisseur carburant pour pouvoir jouer la concurrence à chaque renouvellement. ` +
      `Côté pneumatiques, je rappellerais bien Michelin pour un contrat-cadre — au-dessus de 8 véhicules ils proposent des conditions intéressantes.\n\n` +
      `— Karim Benali`,
    ttlDays: TTL_DAYS,
  };
}

function buildHelenePichon(state, kpis) {
  const { year, vehiclesTotal } = kpis;
  if (vehiclesTotal < 8) return null;
  return {
    senderType: 'support',
    senderName: 'Hélène Pichon (Resp. achats)',
    senderRole: 'role-helene-pichon',
    tone: 'celebratory',
    kind: 'info',
    subject: `Achats stratégiques ${year} — contrats-cadres opérationnels`,
    body:
      `Patron,\n\n` +
      `${year} a été l'année des **contrats-cadres** : carburant (-10 %), maintenance (-8 %) et pièces détachées. ` +
      `Sur ${vehiclesTotal} véhicules, ça représente environ ${fmt(vehiclesTotal * 1200)} € d'économies annuelles.\n\n` +
      `Pour ${year + 1}, je négocie : (1) renouvellement carburant en visant -12 %, (2) un cadre pneus avec Michelin/Continental, (3) un cadre lubrifiants avec Total. Avec votre accord, je lance les RFP en janvier.\n\n` +
      `— Hélène Pichon`,
    ttlDays: TTL_DAYS,
  };
}

function buildPierreMarchetti(state, kpis) {
  // Tristan Midal already has TWO dedicated yearly messages :
  //  • the celebratory bonus modal (when auto-prov + reserve refund)
  //  • the "coup de gueule" rant (when auto-prov was off and incidents)
  // So in the year-end digest he stays SILENT — let his other channels speak.
  return null;
}

function buildKarimSefrioui(state, kpis) {
  const { year, driversTotal } = kpis;
  if (driversTotal < 5) return null;
  return {
    senderType: 'support',
    senderName: 'Karim Sefrioui (Chasseur de tête)',
    senderRole: 'role-karim-sefrioui',
    tone: 'advice',
    kind: 'info',
    subject: `Sourcing ${year} — pipeline talents`,
    body:
      `Patron,\n\n` +
      `Au compteur fin ${year}, on a ${driversTotal} chauffeurs en poste. ` +
      `J'ai dans mon carnet **3 profils premium** (CE + ADR + expérience longue distance > 8 ans) ` +
      `intéressés par un nouveau poste si l'opportunité se présente.\n\n` +
      `Si vous prévoyez 2 acquisitions véhicules en ${year + 1}, je peux lancer les approches dès maintenant — ` +
      `un chauffeur senior débauché, ça prend 60-90 jours en moyenne. À votre signal.\n\n` +
      `— Karim Sefrioui`,
    ttlDays: TTL_DAYS,
  };
}

function buildLucasMercier(state, kpis) {
  const { year, reputation } = kpis;
  return {
    senderType: 'support',
    senderName: 'Lucas Mercier (Hotliner)',
    senderRole: 'role-lucas-mercier',
    tone: reputation >= 60 ? 'celebratory' : 'advice',
    kind: 'info',
    subject: `Service client ${year} — bilan de ligne`,
    body:
      `Patron,\n\n` +
      `J'ai pris environ 80 appels client cette année. ` +
      `Les motifs récurrents : suivi de livraison (45 %), modification créneaux (25 %), réclamations factures (15 %), divers (15 %).\n\n` +
      (reputation >= 60
        ? `Réputation à ${reputation}/100 — la majorité raccroche satisfaite.`
        : `Réputation à ${reputation}/100 — il faudrait qu'on outille mieux la réponse aux réclamations (suivi mail, délai de retour fixe).`) +
      `\n\nPour ${year + 1}, je propose un script d'accueil unifié + un mini-CRM appels pour ne plus perdre un suivi.\n\n` +
      `— Lucas Mercier`,
    ttlDays: TTL_DAYS,
  };
}

function buildInesBouchard(state, kpis) {
  const { year, reputation, reputationDelta } = kpis;
  const tone = reputationDelta >= 0 ? 'celebratory' : 'warning';
  return {
    senderType: 'support',
    senderName: 'Inès Bouchard (Resp. service client)',
    senderRole: 'role-ines-bouchard',
    tone,
    kind: 'info',
    subject: `Service client ${year} — pilotage qualité`,
    body:
      `Patron,\n\n` +
      `Bilan qualité ${year} : réputation à **${reputation}/100** (${reputationDelta >= 0 ? '+' : ''}${reputationDelta} pts). ` +
      (tone === 'celebratory'
        ? `Le processus de gestion des réclamations tient la route.`
        : `Plusieurs clients ont exprimé leur insatisfaction — il faut qu'on revoie les indicateurs SLA.`) +
      `\n\n` +
      `Pour ${year + 1}, je propose un **NPS trimestriel** (5 questions, 2 min) sur tous nos clients actifs. Données concrètes pour piloter vraiment, plutôt qu'au feeling.\n\n` +
      `— Inès Bouchard`,
    ttlDays: TTL_DAYS,
  };
}

function buildMehdiFerhat(state, kpis) {
  const { year, breakdownsThisYear, vehiclesTotal } = kpis;
  if (vehiclesTotal === 0) return null;
  return {
    senderType: 'support',
    senderName: 'Mehdi Ferhat (Mécanicien)',
    senderRole: 'role-mehdi-ferhat',
    tone: 'advice',
    kind: 'info',
    subject: `Atelier ${year} — points méca de l'année`,
    body:
      `Patron,\n\n` +
      `Bilan terrain ${year} : ${breakdownsThisYear} panne${breakdownsThisYear !== 1 ? 's' : ''} ` +
      `sur ${vehiclesTotal} véhicule${vehiclesTotal > 1 ? 's' : ''}. ` +
      `On a tenu malgré les charges. ${breakdownsThisYear > 0 ? "J'ai surtout vu passer des pannes liées à l'usure générale (freins, embrayages, suspensions)." : "Aucun gros incident, c'est plutôt rare."}\n\n` +
      `Pour ${year + 1}, je propose **un cycle de révisions préventives systématiques** tous les 40 000 km — pas juste à la panne. C'est ce que ferait Bruno s'il était là.\n\n` +
      `— Mehdi Ferhat`,
    ttlDays: TTL_DAYS,
  };
}

/* ─────────────────────────────────────────────────────────────────────
 * Main entry — orchestrate per-role builders, return a (possibly empty)
 * array of Messagerie payloads.
 * ───────────────────────────────────────────────────────────────────── */

/**
 * @param {object} state       live store state (must expose hiredSupport).
 * @param {object} annualReport the latest annual report payload (year + KPIs).
 * @param {object} extras      optional extras: { breakdownsThisYear, firedDriversCount, attritionThisYear, totalIsPaid, isOptimizationsActive, reputationDelta, notorietyDelta }
 * @returns {Array<object>}    messages ready for pushCustomMessage.
 */
export function buildSupportYearlyDigest(state, annualReport, extras = {}) {
  if (!state || !annualReport) return [];
  const kpis = {
    year: annualReport.year,
    totalRevenue: annualReport.totalRevenue || 0,
    totalExpenses: annualReport.totalExpenses || 0,
    netResult: annualReport.netResult || 0,
    contractsCompleted: annualReport.contractsCompleted || 0,
    vehiclesTotal: annualReport.vehiclesTotal || 0,
    driversTotal: annualReport.driversTotal || 0,
    reputation: annualReport.reputation || 0,
    notoriety: annualReport.notoriety || 0,
    treasuryDelta: annualReport.treasuryDelta || 0,
    // 22/07/2026 — panier moyen réel des contrats livrés (ledger).
    avgContractValue: annualReport.avgContractValue ?? extras.avgContractValue ?? null,
    // Year-over-year deltas — caller can pass them in `extras`.
    reputationDelta: extras.reputationDelta || 0,
    notorietyDelta: extras.notorietyDelta || 0,
    breakdownsThisYear: extras.breakdownsThisYear || 0,
    firedDriversCount: extras.firedDriversCount || 0,
    attritionThisYear: extras.attritionThisYear || 0,
    totalIsPaid: extras.totalIsPaid || 0,
    isOptimizationsActive: extras.isOptimizationsActive || 0,
    // 17/06/2026 (Lot 4) — KPIs additionnels pour les bilans enrichis :
    transfersInitiatedThisYear: extras.transfersInitiatedThisYear || 0,
    holidaysGrantedThisYear: extras.holidaysGrantedThisYear || 0,
    holidayDriversWentOnLeaveThisYear: extras.holidayDriversWentOnLeaveThisYear || 0,
    vehiclesScrappedThisYear: extras.vehiclesScrappedThisYear || 0,
    fuelTheftsResolvedThisYear: extras.fuelTheftsResolvedThisYear || 0,
  };

  const messages = [];
  const tryAdd = (roleId, builder) => {
    if (!isHired(state, roleId)) return;
    try {
      const msg = builder(state, kpis);
      if (msg) messages.push(msg);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(`[supportYearlyDigest] ${roleId} builder failed:`, err);
    }
  };

  tryAdd('role-nathalie-robert', buildNathalieRobert);
  tryAdd('role-christophe-faure', buildChristopheFaure);
  tryAdd('role-lea-bertrand', buildLeaBertrand);
  tryAdd('role-marc-dupuis', buildMarcDupuis);
  tryAdd('role-sylvie-marchand', buildSylvieMarchand);
  tryAdd('role-yanis-elkaroui', buildYanisElKaroui);
  tryAdd('role-sherazade-fetitou', buildCamilleRoussel);
  tryAdd('role-thomas-vidal', buildThomasVidal);
  tryAdd('role-aicha-diallo', buildAichaDiallo);
  tryAdd('role-karim-benali', buildKarimBenali);
  tryAdd('role-helene-pichon', buildHelenePichon);
  tryAdd('role-sophie-lambert', buildSophieLambert);
  tryAdd('role-pierre-marchetti', buildPierreMarchetti);
  tryAdd('role-nadia-cheikh', buildNadiaCheikh);
  tryAdd('role-karim-sefrioui', buildKarimSefrioui);
  tryAdd('role-lucas-mercier', buildLucasMercier);
  tryAdd('role-ines-bouchard', buildInesBouchard);
  tryAdd('role-mehdi-ferhat', buildMehdiFerhat);
  tryAdd('role-bruno-lavergne', buildBrunoLavergne);

  return messages;
}

// Re-export individual builders for unit-testing each in isolation.
export const _builders = {
  buildNathalieRobert,
  buildChristopheFaure,
  buildLeaBertrand,
  buildMarcDupuis,
  buildSylvieMarchand,
  buildYanisElKaroui,
  buildCamilleRoussel,
  buildThomasVidal,
  buildAichaDiallo,
  buildKarimBenali,
  buildHelenePichon,
  buildSophieLambert,
  buildPierreMarchetti,
  buildNadiaCheikh,
  buildKarimSefrioui,
  buildLucasMercier,
  buildInesBouchard,
  buildMehdiFerhat,
  buildBrunoLavergne,
};
